import { useState } from 'react';
import { X, Upload, Download, Mail, Copy } from 'lucide-react';
import { Button } from '../../../components/ui/button';

interface InvitationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSendInvitations: (emails: string[], message: string) => void;
}

export const InvitationModal = ({ isOpen, onClose, onSendInvitations }: InvitationModalProps) => {
  const [step, setStep] = useState(1);
  const [textAreaContent, setTextAreaContent] = useState('');
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [message, setMessage] = useState('');

  if (!isOpen) return null;

  // Función para extraer emails del texto
  const extractEmailsFromText = (text: string): string[] => {
    const emailRegex = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g;
    const matches = text.match(emailRegex);
    return matches ? [...new Set(matches)] : [];
  };

  const copyExtractedEmails = async () => {
    const extractedEmails = extractEmailsFromText(textAreaContent);
    try {
      await navigator.clipboard.writeText(extractedEmails.join('\n'));
    } catch (err) {
      console.error('Failed to copy emails: ', err);
    }
  };

  const clearTextArea = () => {
    setTextAreaContent('');
  };

  const handleNextStep = () => {
    setStep(2);
  };

  const handleBackStep = () => {
    setStep(1);
  };

  const handleFinalSubmit = () => {
    const validEmails = extractEmailsFromText(textAreaContent);
    
    if (validEmails.length > 0) {
      onSendInvitations(validEmails, message);
      onClose();
      // Reset for next time
      setStep(1);
      setMessage('');
      setTextAreaContent('');
      setCsvFile(null);
    }
  };

  const handleCsvUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.type === 'text/csv') {
      setCsvFile(file);
      // Leer el archivo CSV
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        setTextAreaContent(content);
      };
      reader.readAsText(file);
    }
  };

  const downloadTemplate = () => {
    const csvContent = 'Name,Email,Phone\nJohn Doe,john@example.com,+1234567890\nJane Smith,jane@example.com,+0987654321';
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'invitation_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  // Calcular emails válidos
  const extractedEmails = extractEmailsFromText(textAreaContent);
  const validEmailCount = extractedEmails.length;

  // Step 1: Email Selection
  if (step === 1) {
    return (
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center w-full z-50">
        <div className="bg-white rounded-lg p-6 w-full mx-4 max-h-[90vh] overflow-y-auto" style={{maxWidth: '42rem'}}>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold">Enviar invitaciones</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X size={24} />
            </button>
          </div>

          {/* Email Section */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <label className="block text-sm font-medium">
                <Mail className="inline mr-2" size={16} />
                Direcciones de email
              </label>
              <div className="flex gap-2">
                {extractedEmails.length > 0 && (
                  <Button 
                    variant='primary'
                    onClick={copyExtractedEmails}
                    className="flex items-center gap-1 text-sm"
                  >
                    <Copy size={14} />
                    Copiar
                  </Button>
                )}
                <Button 
                  variant='ghost'
                  onClick={clearTextArea}
                  className="flex items-center gap-1 text-sm text-red-600 hover:text-red-700"
                >
                  Limpiar
                </Button>
              </div>
            </div>
            
            <textarea
              value={textAreaContent}
              onChange={(e) => setTextAreaContent(e.target.value)}
              placeholder="Pegá emails aquí, uno por línea, o copiá desde cualquier fuente. Detectaremos las direcciones automáticamente..."
              className="w-full border rounded-lg px-3 py-3 text-sm focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
              rows={8}
            />
            
            {extractedEmails.length > 0 && (
              <div className="mt-3 p-3 bg-green-50 rounded-lg">
                <p className="text-sm font-medium text-green-800 mb-2">
                  {extractedEmails.length} email{extractedEmails.length !== 1 ? 's' : ''} detectados:
                </p>
                <div className="max-h-32 overflow-y-auto">
                  {extractedEmails.map((email, index) => (
                    <div key={index} className="text-sm text-green-700 py-1">
                      {email}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Divider - solo mostrar si no hay contenido */}
          {!textAreaContent.trim() && (
            <div className="relative mb-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="bg-white px-3 text-gray-500">O</span>
              </div>
            </div>
          )}

          {/* CSV Upload Section */}
          {!textAreaContent.trim() && (
            <div className="mb-6">
              <label className="block text-sm font-medium mb-3">
                Subir archivo CSV
              </label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                <Upload className="mx-auto mb-2 text-gray-400" size={24} />
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleCsvUpload}
                  className="hidden"
                  id="csv-upload"
                />
                <label
                  htmlFor="csv-upload"
                  className="cursor-pointer text-primary hover:text-primary/80"
                >
                  Elegir archivo CSV
                </label>
                {csvFile && (
                  <p className="mt-2 text-sm text-gray-600">
                    Seleccionado: {csvFile.name}
                  </p>
                )}
              </div>
              
              <button
                onClick={downloadTemplate}
                className="w-full mt-3 flex items-center justify-center gap-2 border border-gray-300 py-2 px-4 rounded-lg hover:bg-gray-50 text-sm"
              >
                <Download size={16} />
                Descargar plantilla CSV
              </button>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button
              variant='outline'
              onClick={onClose}
              className="flex-1 border border-gray-300 py-2 px-4 rounded-lg hover:bg-gray-50"
            >
              Cancelar
            </Button>
            
            <Button
              variant='primary'
              onClick={handleNextStep}
              disabled={validEmailCount === 0}
              className="flex-1 bg-accent text-white py-2 px-4 rounded-lg hover:bg-accent/90 disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              Siguiente ({validEmailCount})
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Step 2: Message Composition
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center w-full z-50">
      <div className="bg-white rounded-lg p-6 w-full mx-4 max-h-[90vh] overflow-y-auto" style={{maxWidth: '42rem'}}>
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold">Enviar invitaciones</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={24} />
          </button>
        </div>

        {/* Email Summary */}
        <div className="mb-4 pt-3 bg-gray-50 rounded-lg">
          <p className="text-sm text-gray-600">
            Enviando a <span className="font-medium">{validEmailCount} destinatario{validEmailCount !== 1 ? 's' : ''}</span>
          </p>
        </div>

        {/* Message Section */}
        <div className="relative">
          <label className="block text-sm font-medium mb-3">
            Mensaje de invitación
          </label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Escribí el mensaje..."
            className="w-full border px-3 py-3 text-sm focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
            rows={6}
            style={{borderRadius: '8px 8px 0px 0px'}}
          />
          <div className="mb-6 bg-gray-200 p-3" style={{position:'relative', top: '-5px', borderRadius: '0px 0px 8px 8px'}}>
            <p className="text-sm text-gray-600">
                <strong>RSVP:</strong> cap.com/invitation
            </p>
          </div>
        </div>

        {/* Auto-approval Notice */}
        <div className="mb-6 bg-purple-50 rounded-lg p-3" style={{maxWidth: 'max-content'}}>
          <p className="text-sm text-purple-600">
            Los invitados serán aprobados automáticamente al aceptar la invitación.
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3">
          <Button
            variant='ghost'
            onClick={handleBackStep}
            className="flex items-center gap-2 border border-gray-300 py-2 px-4 rounded-lg hover:bg-gray-50"
          >
            Volver
          </Button>
          
          <Button
            onClick={handleFinalSubmit}
            className="flex-1 bg-accent text-white py-2 px-4 rounded-lg hover:bg-accent/90"
          >
            Enviar invitaciones ({validEmailCount})
          </Button>
        </div>
      </div>
    </div>
  );
};